// Brand membership & invite roles/statuses. Dumb constants (no imports) — safe to require anywhere, incl. model files.

const ROLES = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER'
};

const MEMBERSHIP_STATUSES = {
  ACTIVE: 'ACTIVE',
  REVOKED: 'REVOKED'
};

const INVITE_STATUSES = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  REVOKED: 'REVOKED'
};

const ROLE_LABELS = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.MEMBER]: 'Member'
};

// Max emails accepted in a single invite batch (enforced on client and server).
const MAX_INVITES_PER_BATCH = 10;

// Elevated roles
const MANAGER_ROLES = [ROLES.OWNER, ROLES.ADMIN];

// Seats a brand gets when nothing it subscribes to says otherwise.
const DEFAULT_TEAM_SEATS = 5;
const UNLIMITED_TEAM_SEATS = 'UNLIMITED';

// Subscription unlock enums that grant seats, and the total each one grants (not an increment). Adding a
// new total means adding it here and to BRAND_UNLOCK_FEATURES in subscription_modules.js — an unlock is a boolean, so a
// number nobody has registered can't be sold.
const TEAM_SEAT_UNLOCKS = {
  TEAM_MANAGEMENT_SEATS_1: 1,
  TEAM_MANAGEMENT_SEATS_6: 6,
  TEAM_MANAGEMENT_SEATS_10: 10,
  TEAM_MANAGEMENT_UNLIMITED_SEATS: UNLIMITED_TEAM_SEATS
};

// What a full team is offered. Each seat add-on is scoped to its segment via addOnForSegments, so a brand
// must be offered the one sold to its tier — another tier's resolves to no offer and dead-ends the upsell
// modal. Launch starts at a single seat and tops up to 6; every other tier starts at 5 and tops up to 10.
const resolveSeatUpsellUnlock = segment => (segment === 'launch' ? 'TEAM_MANAGEMENT_SEATS_6' : 'TEAM_MANAGEMENT_SEATS_10');

/*
  Seats a brand may fill (active members + pending invites), shared by client and server so both agree.
  `override` is BrandSettings.featureFlags.teamManagementConfiguredSeats — absent means "no override, use
  the subscription", and it wins in both directions when set, so a support-set cap can also lower a brand
  below what its package grants.

  Multiple grants resolve to the largest, not a sum: an offer carrying both a total and unlimited grants
  unlimited rather than an arbitrary combination. The default is only the fallback for a brand whose offers
  grant no seats at all — it is deliberately NOT a floor, because entry tiers grant fewer seats than it and
  maxing against it would silently promote them.
*/
const resolveTeamSeats = ({ unlocks = [], override } = {}) => {
  if (override === UNLIMITED_TEAM_SEATS) return UNLIMITED_TEAM_SEATS;
  if (typeof override === 'number' && override > 0) return override;

  const grants = unlocks.map(unlock => TEAM_SEAT_UNLOCKS[unlock]).filter(grant => grant !== undefined);
  if (!grants.length) return DEFAULT_TEAM_SEATS;
  if (grants.includes(UNLIMITED_TEAM_SEATS)) return UNLIMITED_TEAM_SEATS;
  return Math.max(...grants);
};

module.exports = {
  ROLES,
  MEMBERSHIP_STATUSES,
  INVITE_STATUSES,
  ROLE_LABELS,
  MANAGER_ROLES,
  MAX_INVITES_PER_BATCH,
  DEFAULT_TEAM_SEATS,
  UNLIMITED_TEAM_SEATS,
  TEAM_SEAT_UNLOCKS,
  TEAM_SEAT_UNLOCK_ENUMS: Object.keys(TEAM_SEAT_UNLOCKS),
  resolveTeamSeats,
  resolveSeatUpsellUnlock,
  ROLE_VALUES: Object.values(ROLES),
  MEMBERSHIP_STATUS_VALUES: Object.values(MEMBERSHIP_STATUSES),
  INVITE_STATUS_VALUES: Object.values(INVITE_STATUSES)
};
